/**
 * SelectionRule Class
 * Handles quantity selection rules for meal specifications
 * Based on qty_rule_design.md specification
 */

export class SelectionRule {
    constructor(data = {}) {
        // Initialize with default "any" rule if no data provided
        if (!data || Object.keys(data).length === 0) {
            this.ruleType = 'any';
            this.min = 0;
            this.max = null;
            this.exact = null;
        } else {
            this.ruleType = data.ruleType || 'any';
            this.min = data.min;
            this.max = data.max;
            this.exact = data.exact;
        }



        // Validate the rule after initialization
        this._validateRule();
    }

    /**
     * Create SelectionRule from legacy minQty/maxQty values
     * @param {number} minQty - Legacy minimum quantity
     * @param {number} maxQty - Legacy maximum quantity
     * @returns {SelectionRule} - New SelectionRule instance
     */
    static fromLegacy(minQty, maxQty) {
        // Convert legacy values to new rule format
        if (minQty === 0 && (maxQty === null || maxQty === undefined)) {
            return new SelectionRule({ ruleType: 'any', min: 0, max: null, exact: null });
        } else if (minQty === maxQty) {
            return new SelectionRule({ ruleType: 'exact', min: null, max: null, exact: minQty });
        } else if (minQty === 0) {
            return new SelectionRule({ ruleType: 'max', min: 0, max: maxQty, exact: null });
        } else if (maxQty === null || maxQty === undefined) {
            return new SelectionRule({ ruleType: 'min', min: minQty, max: null, exact: null });
        } else {
            return new SelectionRule({ ruleType: 'between', min: minQty, max: maxQty, exact: null });
        }
    }

    /**
     * Create SelectionRule from rule type and value(s)
     * @param {string} ruleType - Type of rule (any, max, exact, min, between)
     * @param {number|null} value1 - First value (depends on rule type)
     * @param {number|null} value2 - Second value (only for 'between' rule)
     * @returns {SelectionRule} - New SelectionRule instance
     */
    static fromRuleType(ruleType, value1 = null, value2 = null) {
        switch (ruleType) {
            case 'any':
                return new SelectionRule({ ruleType: 'any', min: 0, max: null, exact: null });
            case 'max':
                return new SelectionRule({ ruleType: 'max', min: 0, max: value1, exact: null });
            case 'exact':
                return new SelectionRule({ ruleType: 'exact', min: null, max: null, exact: value1 });
            case 'min':
                return new SelectionRule({ ruleType: 'min', min: value1, max: null, exact: null });
            case 'between':
                return new SelectionRule({ ruleType: 'between', min: value1, max: value2, exact: null });
            default:
                throw new Error(`Invalid rule type: ${ruleType}`);
        }
    }

    /**
     * Validate the current rule configuration
     * @private
     */
    _validateRule() {
        const validRuleTypes = ['any', 'max', 'exact', 'min', 'between'];
        
        if (!validRuleTypes.includes(this.ruleType)) {
            throw new Error(`Invalid rule type: ${this.ruleType}`);
        }

        switch (this.ruleType) {
            case 'any':
                // Temporarily disabled for debugging
                // if (this.min !== 0 || this.max !== null || this.exact !== null) {
                //     throw new Error('Any rule must have min=0, max=null, exact=null');
                // }
                break;
            case 'max':
                if (this.min !== 0 || this.max === null || this.exact !== null) {
                    throw new Error('Max rule must have min=0, max=number, exact=null');
                }
                if (this.max < 1) {
                    throw new Error('Max value must be at least 1');
                }
                break;
            case 'exact':
                if (this.min !== null || this.max !== null || this.exact === null) {
                    throw new Error('Exact rule must have min=null, max=null, exact=number');
                }
                if (this.exact < 0) {
                    throw new Error('Exact value must be at least 0');
                }
                break;
            case 'min':
                if (this.min === null || this.max !== null || this.exact !== null) {
                    throw new Error('Min rule must have min=number, max=null, exact=null');
                }
                if (this.min < 0) {
                    throw new Error('Min value must be at least 0');
                }
                break;
            case 'between':
                if (this.min === null || this.max === null || this.exact !== null) {
                    throw new Error('Between rule must have min=number, max=number, exact=null');
                }
                if (this.min < 0 || this.max < 1) {
                    throw new Error('Between values must be valid (min >= 0, max >= 1)');
                }
                if (this.min >= this.max) {
                    throw new Error('Min value must be less than max value');
                }
                break;
        }
    }

    /**
     * Get the display label for this rule
     * @returns {string} - Human-readable label
     */
    getDisplayLabel() {
        switch (this.ruleType) {
            case 'any':
                return '選擇任意數量';
            case 'max':
                return `最多選擇 ${this.max} 項`;
            case 'exact':
                return `必須選擇 ${this.exact} 項`;
            case 'min':
                return `至少選擇 ${this.min} 項`;
            case 'between':
                return `選擇 ${this.min} 至 ${this.max} 項`;
            default:
                return '未知規則';
        }
    }

    /**
     * Get the primary value for this rule (for UI input)
     * @returns {number|null} - The main value to display in UI
     */
    getPrimaryValue() {
        switch (this.ruleType) {
            case 'any':
                return null;
            case 'max':
                return this.max;
            case 'exact':
                return this.exact;
            case 'min':
                return this.min;
            case 'between':
                return this.min;
            default:
                return null;
        }
    }

    /**
     * Get the secondary value for this rule (for UI input, only used by 'between')
     * @returns {number|null} - The secondary value to display in UI
     */
    getSecondaryValue() {
        return this.ruleType === 'between' ? this.max : null;
    }

    /**
     * Validate if a given quantity satisfies this rule
     * @param {number} quantity - The quantity to validate
     * @returns {boolean} - True if quantity is valid
     */
    isValidQuantity(quantity) {
        if (typeof quantity !== 'number' || quantity < 0) {
            return false;
        }

        switch (this.ruleType) {
            case 'any':
                return true; // Any quantity is valid
            case 'max':
                return quantity <= this.max;
            case 'exact':
                return quantity === this.exact;
            case 'min':
                return quantity >= this.min;
            case 'between':
                return quantity >= this.min && quantity <= this.max;
            default:
                return false;
        }
    }

    /**
     * Get validation error message for invalid quantity
     * @param {number} quantity - The invalid quantity
     * @returns {string} - Error message
     */
    getValidationError(quantity) {
        if (!this.isValidQuantity(quantity)) {
            switch (this.ruleType) {
                case 'max':
                    return `最多只能選擇 ${this.max} 項`;
                case 'exact':
                    return `必須選擇 ${this.exact} 項`;
                case 'min':
                    return `至少需要選擇 ${this.min} 項`;
                case 'between':
                    return `需要選擇 ${this.min} 至 ${this.max} 項`;
                default:
                    return '選擇數量無效';
            }
        }
        return null;
    }

    /**
     * Convert to plain object for JSON serialization
     * @returns {Object} - Plain object representation
     */
    toJSON() {
        return {
            ruleType: this.ruleType,
            min: this.min,
            max: this.max,
            exact: this.exact
        };
    }

    /**
     * Convert to legacy minQty/maxQty format for backward compatibility
     * @returns {Object} - Legacy format { minQty, maxQty }
     */
    toLegacy() {
        switch (this.ruleType) {
            case 'any':
                return { minQty: 0, maxQty: 999 }; // Use large number for "unlimited"
            case 'max':
                return { minQty: 0, maxQty: this.max };
            case 'exact':
                return { minQty: this.exact, maxQty: this.exact };
            case 'min':
                return { minQty: this.min, maxQty: 999 }; // Use large number for "unlimited"
            case 'between':
                return { minQty: this.min, maxQty: this.max };
            default:
                return { minQty: 1, maxQty: 1 };
        }
    }

    /**
     * Clone this rule
     * @returns {SelectionRule} - New instance with same values
     */
    clone() {
        return new SelectionRule(this.toJSON());
    }

    /**
     * Check if this rule equals another rule
     * @param {SelectionRule} other - Other rule to compare
     * @returns {boolean} - True if rules are equal
     */
    equals(other) {
        if (!(other instanceof SelectionRule)) {
            return false;
        }
        return (
            this.ruleType === other.ruleType &&
            this.min === other.min &&
            this.max === other.max &&
            this.exact === other.exact
        );
    }
}

export default SelectionRule;
